import { useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { 
  Loader2, Users, Upload, X, Copy, CheckCircle, AlertCircle,
  Type, Link2, Eye, Globe, Lock, Star, Sparkles, AlertTriangle, Trash2,
  Building2, User, Landmark, Handshake, UserPlus
} from 'lucide-react'
import type { Group, GroupType } from './types'

interface SettingsTabProps {
  group: Group
  // Form state
  name: string
  onNameChange: (value: string) => void
  icon: string | null
  onIconSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveIcon: () => void
  groupType: GroupType
  onGroupTypeChange: (value: GroupType) => void
  showOrganisers: boolean
  onShowOrganisersChange: (value: boolean) => void
  isPublic: boolean
  onIsPublicChange: (value: boolean) => void
  isFeatured: boolean
  onIsFeaturedChange: (value: boolean) => void
  allowJoinRequests: boolean
  onAllowJoinRequestsChange: (value: boolean) => void
  // State
  hasChanges: boolean
  saving: boolean
  error: string | null
  success: boolean
  urlCopied: boolean
  onCopyUrl: () => void
  onSave: () => void
  // Delete
  isOwner: boolean
  onOpenDeleteDialog: () => void
  // Group URL
  groupUrl: string
}

const GROUP_TYPES: { value: GroupType; label: string; icon: typeof Building2; description: string }[] = [
  { value: 'club', label: 'Club', icon: Building2, description: 'A sports club or society' },
  { value: 'individual', label: 'Individual', icon: User, description: 'Personal league organizer' },
  { value: 'organisation', label: 'Organisation', icon: Landmark, description: 'Company or institution' },
  { value: 'community', label: 'Community', icon: Users, description: 'Community group or meetup' },
  { value: 'collaboration', label: 'Collaboration', icon: Handshake, description: 'Multi-group partnership' },
]

export function SettingsTab({
  group,
  name,
  onNameChange,
  icon,
  onIconSelect,
  onRemoveIcon,
  groupType,
  onGroupTypeChange,
  showOrganisers,
  onShowOrganisersChange,
  isPublic,
  onIsPublicChange,
  isFeatured,
  onIsFeaturedChange,
  allowJoinRequests,
  onAllowJoinRequestsChange,
  hasChanges,
  saving,
  error,
  success,
  urlCopied,
  onCopyUrl,
  onSave,
  isOwner,
  onOpenDeleteDialog,
  groupUrl,
}: SettingsTabProps) {
  const iconInputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="space-y-6">
      {/* Group Preview Card */}
      <Card className="overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-5">
            <div className="relative group">
              {icon?.startsWith('data:') || icon?.startsWith('https://') ? (
                <img
                  src={icon}
                  alt="Group icon"
                  className="h-24 w-24 rounded-2xl object-cover ring-4 ring-muted shadow-lg"
                />
              ) : (
                <div className="h-24 w-24 rounded-2xl bg-muted ring-4 ring-muted shadow-lg flex items-center justify-center text-4xl">
                  {icon || '🏸'}
                </div>
              )}
              <input
                ref={iconInputRef}
                type="file"
                accept="image/*"
                onChange={onIconSelect}
                className="hidden"
                id="group-icon-upload"
              />
              <Label 
                htmlFor="group-icon-upload" 
                className="absolute inset-0 rounded-2xl bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
              >
                <div className="text-white text-center">
                  <Upload className="h-6 w-6 mx-auto mb-1" />
                  <span className="text-xs font-medium">Change</span>
                </div>
              </Label>
              {(icon?.startsWith('data:') || icon?.startsWith('https://')) && (
                <button
                  onClick={onRemoveIcon}
                  className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 shadow-md transition-transform hover:scale-110"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold truncate">{name || 'Group Name'}</h2>
              <p className="text-muted-foreground mt-1 flex items-center gap-2">
                <Users className="h-4 w-4" />
                {group?.memberCount || 0} members
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Square image recommended • Max 2.5MB
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Group Identity Section */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Type className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Group Identity</CardTitle>
              <CardDescription>Basic information about your group</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Group Name */}
          <div className="space-y-2">
            <Label htmlFor="group-name" className="text-sm font-medium">Group Name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Enter group name"
              maxLength={100}
              className="h-11"
            />
          </div>

          {/* Group Type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Group Type</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {GROUP_TYPES.map((type) => {
                const Icon = type.icon
                const isSelected = groupType === type.value
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => onGroupTypeChange(type.value)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : ''}`}>
                        {type.label}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              This helps players understand what kind of group they're joining
            </p>
          </div>

          {/* Group URL */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              Group URL
            </Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input 
                  value={groupUrl} 
                  readOnly 
                  className="h-11 pr-24 bg-muted/50 text-muted-foreground font-mono text-sm"
                />
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={onCopyUrl} 
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8"
                >
                  {urlCopied ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1.5 text-green-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1.5" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              This URL cannot be changed after creation
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Display Settings Section */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Eye className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Display Settings</CardTitle>
              <CardDescription>Control what appears on your group profile</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Public Group Toggle */}
          <div className="flex items-center justify-between rounded-xl border bg-muted/30 p-4 hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                isPublic 
                  ? 'bg-green-500/10' 
                  : 'bg-gray-500/10'
              }`}>
                {isPublic ? (
                  <Globe className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Lock className="h-5 w-5 text-gray-500" />
                )}
              </div>
              <div className="space-y-0.5">
                <Label htmlFor="is-public" className="text-sm font-medium cursor-pointer">
                  Public Group Profile
                </Label>
                <p className="text-sm text-muted-foreground">
                  {isPublic 
                    ? "Anyone can view this groups and its leagues"
                    : "Only members can view this group"
                  }
                </p>
              </div>
            </div>
            <Switch
              id="is-public"
              checked={isPublic}
              onCheckedChange={onIsPublicChange}
            />
          </div>

          {/* Allow Join Requests Toggle - Only shown for public groups */}
          {isPublic && (
            <div className="flex items-center justify-between rounded-xl border bg-muted/30 p-4 hover:bg-muted/50 transition-colors ml-6">
              <div className="flex items-center gap-4">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  allowJoinRequests 
                    ? 'bg-blue-500/10' 
                    : 'bg-gray-500/10'
                }`}>
                  <UserPlus className={`h-5 w-5 ${
                    allowJoinRequests 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : 'text-gray-500'
                  }`} />
                </div>
                <div className="space-y-0.5">
                  <Label htmlFor="allow-join-requests" className="text-sm font-medium cursor-pointer">
                    Allow Join Requests
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {allowJoinRequests 
                      ? "Visitors can request to join this group"
                      : "Join requests are disabled"
                    }
                  </p>
                </div>
              </div>
              <Switch
                id="allow-join-requests"
                checked={allowJoinRequests}
                onCheckedChange={onAllowJoinRequestsChange}
              />
            </div>
          )}

          {/* Show Organisers Toggle */}
          <div className="flex items-center justify-between rounded-xl border bg-muted/30 p-4 hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Star className="h-5 w-5 text-amber-500" />
              </div>
              <div className="space-y-0.5">
                <Label htmlFor="show-organisers" className="text-sm font-medium cursor-pointer">
                  Show Organisers
                </Label>
                <p className="text-sm text-muted-foreground">
                  Display group organisers on the group profile
                </p>
              </div>
            </div>
            <Switch
              id="show-organisers"
              checked={showOrganisers}
              onCheckedChange={onShowOrganisersChange}
            />
          </div>

          {/* Featured Group Toggle - Super Admin Only, requires Public */}
          {group?.isSuperAdmin && (
            <div className={`flex items-center justify-between rounded-xl border-2 border-dashed p-4 ${
              isPublic 
                ? 'border-purple-300 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20' 
                : 'border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/20 opacity-60'
            }`}>
              <div className="flex items-center gap-4">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  isFeatured && isPublic
                    ? 'bg-purple-500/20' 
                    : 'bg-gray-500/10'
                }`}>
                  <Sparkles className={`h-5 w-5 ${
                    isFeatured && isPublic
                      ? 'text-purple-600 dark:text-purple-400' 
                      : 'text-gray-500'
                  }`} />
                </div>
                <div className="space-y-0.5">
                  <Label htmlFor="is-featured" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    Featured Group
                    <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">
                      Super Admin
                    </span>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {!isPublic
                      ? "Group must be public to be featured"
                      : isFeatured 
                        ? "This group is featured on the home page"
                        : "Feature this group on the home page"
                    }
                  </p>
                </div>
              </div>
              <Switch
                id="is-featured"
                checked={isFeatured && isPublic}
                onCheckedChange={onIsFeaturedChange}
                disabled={!isPublic}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error/Success Messages & Save */}
      <div className="space-y-4">
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-destructive/20 flex items-center justify-center shrink-0">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="font-medium text-destructive">Error saving settings</p>
              <p className="text-sm text-destructive/80">{error}</p>
            </div>
          </div>
        )}
        
        {success && (
          <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center shrink-0">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="font-medium text-green-600 dark:text-green-400">Settings saved</p>
              <p className="text-sm text-green-600/80 dark:text-green-400/80">Your changes have been saved successfully</p>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            {hasChanges ? (
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                You have unsaved changes
              </span>
            ) : (
              'All changes saved'
            )}
          </p>
          <Button
            onClick={onSave}
            disabled={saving || !hasChanges}
            size="lg"
            className="min-w-[140px]"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>

      {/* Danger Zone - Owner Only */}
      {isOwner && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-destructive/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-lg text-destructive">Danger Zone</CardTitle>
                <CardDescription>Irreversible actions that affect this group</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between rounded-xl border border-destructive/30 bg-background p-4">
              <div className="space-y-0.5">
                <p className="font-medium">Delete this group</p>
                <p className="text-sm text-muted-foreground">
                  Permanently delete this group, all leagues, seasons, matches, and member data. This cannot be undone.
                </p>
              </div>
              <Button 
                variant="destructive" 
                onClick={onOpenDeleteDialog}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Group
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
